import { useState } from "react";
import {
  Alert,
  Button,
  Combobox,
  FormField,
  TextArea,
  TextInput,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateAppConfigMutation } from "../services/useCreateAppConfigMutation";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type DraftType = "boolean" | "integer" | "string" | "url";
type DraftScope = "runtime" | "boot";

type Draft = {
  key: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  type: DraftType;
  scope: DraftScope;
  value: string;
  minInt: string;
  maxInt: string;
};

const EMPTY_DRAFT: Draft = {
  key: "",
  nameEn: "",
  nameAr: "",
  descriptionEn: "",
  descriptionAr: "",
  type: "string",
  scope: "runtime",
  value: "",
  minInt: "",
  maxInt: "",
};

// Modal for creating a custom (non-registry) key. Form mirrors the
// metadata shape registry keys carry in code: bilingual name + description,
// type, scope, value, integer bounds. The backend stores all of it in the
// expanded `app_configs` row so the listing can render custom keys with
// the same affordances as registry keys.
export function AddAppConfigDialog({ isOpen, onClose }: Props) {
  const t = useTranslations();
  const { mutateAsync, isPending } = useCreateAppConfigMutation();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  useDialogDismiss(isOpen, onClose);

  if (!isOpen) return null;

  const update = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await mutateAsync({
        key: draft.key.trim(),
        nameEn: draft.nameEn.trim(),
        nameAr: draft.nameAr.trim(),
        descriptionEn: draft.descriptionEn.trim(),
        descriptionAr: draft.descriptionAr.trim(),
        type: draft.type,
        scope: draft.scope,
        value: draft.value,
        ...(draft.type === "integer" && draft.minInt.trim().length > 0
          ? { minInt: Number(draft.minInt) }
          : {}),
        ...(draft.type === "integer" && draft.maxInt.trim().length > 0
          ? { maxInt: Number(draft.maxInt) }
          : {}),
      });
      setDraft(EMPTY_DRAFT);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiJsonClientError
          ? err.message || t(keys.errors.generic)
          : t(keys.errors.generic),
      );
    }
  };

  const isInteger = draft.type === "integer";
  const isBoolean = draft.type === "boolean";

  const valueRequired =
    draft.key.length === 0 ||
    draft.nameEn.length === 0 ||
    draft.nameAr.length === 0 ||
    draft.value.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-6 shadow-[var(--shadow-lg)]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
          {t(keys.appConfigs.addTitle)}
        </h2>
        <p className="mt-1 text-xs text-[color:var(--text-muted)]">
          {t(keys.appConfigs.addBody)}
        </p>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          <FormField
            name="key"
            label={t(keys.appConfigs.addKeyLabel)}
            helperText={t(keys.appConfigs.addKeyHelper)}
            required
          >
            <TextInput
              name="key"
              value={draft.key}
              autoFocus
              placeholder="MY_CUSTOM_FLAG"
              onChange={(event) =>
                update(
                  "key",
                  event.target.value.toUpperCase().replace(/\s+/g, "_"),
                )
              }
            />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              name="nameEn"
              label={t(keys.appConfigs.addNameEnLabel)}
              required
            >
              <TextInput
                name="nameEn"
                value={draft.nameEn}
                onChange={(event) => update("nameEn", event.target.value)}
              />
            </FormField>
            <FormField
              name="nameAr"
              label={t(keys.appConfigs.addNameArLabel)}
              required
            >
              <TextInput
                name="nameAr"
                dir="rtl"
                value={draft.nameAr}
                onChange={(event) => update("nameAr", event.target.value)}
              />
            </FormField>
          </div>

          <FormField
            name="descriptionEn"
            label={t(keys.appConfigs.addDescriptionEnLabel)}
          >
            <TextArea
              name="descriptionEn"
              rows={2}
              value={draft.descriptionEn}
              onChange={(event) => update("descriptionEn", event.target.value)}
            />
          </FormField>
          <FormField
            name="descriptionAr"
            label={t(keys.appConfigs.addDescriptionArLabel)}
          >
            <TextArea
              name="descriptionAr"
              rows={2}
              dir="rtl"
              value={draft.descriptionAr}
              onChange={(event) => update("descriptionAr", event.target.value)}
            />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              name="type"
              label={t(keys.appConfigs.addTypeLabel)}
              required
            >
              <Combobox
                value={draft.type}
                onChange={(value) => {
                  if (value) update("type", value as DraftType);
                }}
                options={[
                  { value: "boolean", label: t(keys.appConfigs.typeBoolean) },
                  { value: "integer", label: t(keys.appConfigs.typeInteger) },
                  { value: "string", label: t(keys.appConfigs.typeString) },
                  { value: "url", label: t(keys.appConfigs.typeUrl) },
                ]}
                aria-label={t(keys.appConfigs.addTypeLabel)}
              />
            </FormField>
            <FormField
              name="scope"
              label={t(keys.appConfigs.addScopeLabel)}
              required
            >
              <Combobox
                value={draft.scope}
                onChange={(value) => {
                  if (value) update("scope", value as DraftScope);
                }}
                options={[
                  { value: "runtime", label: "runtime" },
                  { value: "boot", label: "boot" },
                ]}
                aria-label={t(keys.appConfigs.addScopeLabel)}
              />
            </FormField>
          </div>

          {isInteger ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField name="minInt" label={t(keys.appConfigs.addMinLabel)}>
                <TextInput
                  name="minInt"
                  type="number"
                  inputMode="numeric"
                  value={draft.minInt}
                  onChange={(event) => update("minInt", event.target.value)}
                />
              </FormField>
              <FormField name="maxInt" label={t(keys.appConfigs.addMaxLabel)}>
                <TextInput
                  name="maxInt"
                  type="number"
                  inputMode="numeric"
                  value={draft.maxInt}
                  onChange={(event) => update("maxInt", event.target.value)}
                />
              </FormField>
            </div>
          ) : null}

          <FormField
            name="value"
            label={t(keys.appConfigs.addValueLabel)}
            required
          >
            {isBoolean ? (
              <Combobox
                value={draft.value || "false"}
                onChange={(value) => update("value", value ?? "false")}
                options={[
                  { value: "true", label: "true" },
                  { value: "false", label: "false" },
                ]}
                aria-label={t(keys.appConfigs.addValueLabel)}
              />
            ) : (
              <TextInput
                name="value"
                type={isInteger ? "number" : "text"}
                inputMode={isInteger ? "numeric" : undefined}
                value={draft.value}
                onChange={(event) => update("value", event.target.value)}
              />
            )}
          </FormField>

          {error ? <Alert message={error} /> : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || valueRequired}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {isPending
                ? t(keys.appConfigs.saving)
                : t(keys.appConfigs.addAction)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
